/**
 * Секция нарисовала заглушки — сообщить и, в dev, показать это глазами.
 *
 * Подключается одной строкой в секции, у которой включён демо-фолбэк. Делает две
 * вещи и обе намеренно:
 *
 * 1. Сообщает событие (секция, промолчавшая ручка, причина) — чтобы молчащая
 *    ручка перестала быть невидимой в проде, пока заглушки не вырезаны.
 * 2. Отдаёт `showDevMarker` — в non-prod секция рисует видимую пометку, и это
 *    ловится при разработке, а не через полгода в поддержке.
 *
 * Композабл НИЧЕГО НЕ СКРЫВАЕТ и не меняет то, что рисуется: решение «показывать
 * заглушку или нет» принимает секция. Здесь только наблюдение.
 */

import { computed, watch, type ComputedRef, type Ref } from 'vue'
import {
  isDevBuild,
  reportDemoCards,
  type DemoCardReason,
} from './demoCardTelemetry'

export interface DemoCardWatchInput<T extends { isDemo?: boolean }> {
  /** 'home.brands', 'search.you-may-like' — как называется место на странице. */
  section: string
  /** Промолчавшая ручка, читаемо: 'GET /v0/recommendations/may-like'. */
  endpoint: string
  cards: Ref<T[]> | ComputedRef<T[]>
  /** Была ли ошибка запроса; иначе причина — пустой ответ. */
  hasError?: Ref<boolean> | ComputedRef<boolean>
}

export function useDemoCardWatch<T extends { isDemo?: boolean }>(
  input: DemoCardWatchInput<T>,
) {
  const demoCount = computed(
    () => input.cards.value.filter((card) => card.isDemo).length,
  )

  const reason = computed<DemoCardReason>(() =>
    input.hasError?.value ? 'error' : 'empty',
  )

  watch(
    demoCount,
    (count) => {
      reportDemoCards({
        section: input.section,
        endpoint: input.endpoint,
        reason: reason.value,
        count,
      })
    },
    { immediate: true },
  )

  return {
    demoCount,
    /** Рисовать ли видимую пометку. Только вне прода. */
    showDevMarker: computed(() => isDevBuild() && demoCount.value > 0),
  }
}
